import {FC, useEffect, useMemo, useState} from 'react'
import moment from 'moment'
import Swal from 'sweetalert2'
import {PageTitle} from '../../../../_metronic/layout/core'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {Button} from '../../../components/Button/Button'
import {Datatable} from '../../../components/Datatable/Datatable'
import {Input} from '../../../components/Input/Input'
import {Loading} from '../../../components/Loading/Loading'
import {ModalWrapper} from '../../../components/Modals/General/ModalWrapper/ModalWrapper'
import useModals from '../../../hooks/useModals'
import {DELETE, GET, PATCH, POST, PUT} from '../../../services/api'
import styles from './hotelpoints.module.css'
import {
  HOTEL_POINTS_SCOPE_LABELS,
  HOTEL_POINTS_VISIBLE_SCOPES,
  HotelPointsRuleScope,
} from './hotelPointsLabels'

type RuleScope = HotelPointsRuleScope

type TabKey = RuleScope | 'ranking'

// Aba Prioridade (ranking) desconsiderada no hotel por agora.
// Para reativar, volte TabKey para RuleScope | 'ranking', ENABLE_PRIORITY_RANKING_TAB = true
// e descomente o item de aba + o painel de ranking abaixo.
const ENABLE_PRIORITY_RANKING_TAB = false

type TargetItem = {
  id: number
  name: string
  status?: boolean
}

type HotelPointsRuleRecord = {
  id: number
  scope: RuleScope
  targetId: number
  targetName: string
  targetData?: Record<string, any> | null
  points: number
  validFrom: string | null
  validTo: string | null
  status?: boolean
}

type HotelPointsRuleForm = {
  scope: RuleScope
  points: number | string
  validFrom: string
  validTo: string
  status: boolean
}

type HotelPointsTableRow = HotelPointsRuleRecord & {
  scopeLabel: string
}

const RULE_SCOPES: RuleScope[] = [...HOTEL_POINTS_VISIBLE_SCOPES]

const DEFAULT_RANKING: RuleScope[] = [
  'service',
  'service_classification',
  'room_service',
  'experience',
  'activity',
  'tour',
  // 'survey',
  'feedback_form',
  'review_form',
]

const scopeLabels: Record<RuleScope, string> = HOTEL_POINTS_SCOPE_LABELS

const rankingLabels: Record<string, string> = {...scopeLabels}

const scopeIcons: Record<RuleScope, string> = {
  service: 'bi-concierge-bell',
  service_classification: 'bi-tags',
  room_service: 'bi-door-open',
  experience: 'bi-stars',
  activity: 'bi-bicycle',
  tour: 'bi-map',
  survey: 'bi-clipboard-check',
  feedback_form: 'bi-chat-square-text',
  review_form: 'bi-star',
}

const scopePageMeta: Record<RuleScope, {title: string; description: string}> = {
  service: {
    title: 'Serviço',
    description: 'Regras aplicadas diretamente em serviços do hotel.',
  },
  service_classification: {
    title: 'Classificação',
    description: 'Regras aplicadas em classificações de serviço.',
  },
  room_service: {
    title: 'Serviços Essenciais',
    description: 'Regras aplicadas em pedidos de serviços essenciais.',
  },
  experience: {
    title: 'Experiência',
    description: 'Regras aplicadas em experiências do hotel.',
  },
  activity: {
    title: 'Atividade',
    description: 'Regras aplicadas em atividades do hotel.',
  },
  tour: {
    title: 'Tour',
    description: 'Regras aplicadas em tours do hotel.',
  },
  survey: {
    title: 'Pesquisa',
    description: 'Regras aplicadas em pesquisas respondidas pelo hóspede.',
  },
  feedback_form: {
    title: 'Pesquisa',
    description: 'Regras aplicadas em formulários de pesquisa respondidos pelo hóspede.',
  },
  review_form: {
    title: 'Opinião',
    description: 'Regras aplicadas em formulários de opinião.',
  },
}

const defaultForm = (scope: RuleScope = 'service'): HotelPointsRuleForm => ({
  scope,
  points: 0,
  validFrom: '',
  validTo: '',
  status: true,
})

const toDateOnly = (value: string | null) => {
  if (!value) return ''
  const parsed = moment(value)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : ''
}

const extractList = (response: any): any[] => {
  if (Array.isArray(response?.data)) return response.data
  if (Array.isArray(response?.dados)) return response.dados
  if (Array.isArray(response)) return response
  return []
}

const getTargetDisplayName = (item: any): string => {
  return (
    item?.targetData?.name ||
    item?.targetData?.title ||
    item?.targetData?.formName ||
    item?.targetName ||
    item?.name ||
    item?.title ||
    item?.formName ||
    '-'
  )
}

const getSearchItemName = (item: any): string =>
  item?.name || item?.title || item?.formName || '-'

const normalizeRule = (item: any): HotelPointsRuleRecord => ({
  id: Number(item?.id ?? 0),
  scope: (item?.typePoints ?? item?.scope ?? 'service') as RuleScope,
  targetId: Number(item?.targetId ?? 0),
  targetName: getTargetDisplayName(item),
  targetData: item?.targetData ?? null,
  points: Number(item?.points ?? 0),
  validFrom: item?.dateInit ?? null,
  validTo: item?.dateEnd ?? null,
  status: Boolean(item?.status),
})

const normalizeTarget = (item: any): TargetItem => ({
  id: Number(item?.id ?? 0),
  name: getSearchItemName(item),
  status: item?.status !== undefined ? Boolean(item.status) : item?.isActive,
})

const matchesQuery = (item: any, query: string) =>
  getSearchItemName(item).toLowerCase().includes(query.toLowerCase())

const ManagementContent: FC = () => {
  const {showWrapperModal, closeWrapperModal, wrapperModal} = useModals()
  const [loading, setLoading] = useState(true)
  const [tableLoading, setTableLoading] = useState(false)
  const [savingRule, setSavingRule] = useState(false)

  const [activeTab, setActiveTab] = useState<TabKey>('service')
  const [specificRules, setSpecificRules] = useState<HotelPointsRuleRecord[]>([])
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<HotelPointsRuleForm>(defaultForm('service'))
  const [selectedTargets, setSelectedTargets] = useState<TargetItem[]>([])
  const [showSelected, setShowSelected] = useState(true)

  const [rankingOrder, setRankingOrder] = useState<string[]>(DEFAULT_RANKING)
  const [rankingLoading, setRankingLoading] = useState(false)

  const [searchQuery, setSearchQuery] = useState('')
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchResults, setSearchResults] = useState<TargetItem[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})

  const currentRules = useMemo(
    () => (activeTab !== 'ranking' ? specificRules.filter((rule) => rule.scope === activeTab) : []),
    [specificRules, activeTab]
  )

  const currentTitle =
    activeTab !== 'ranking' ? scopePageMeta[activeTab].title : 'Ordem de Prioridade'
  const currentDescription =
    activeTab !== 'ranking'
      ? scopePageMeta[activeTab].description
      : 'Defina qual regra o sistema deve avaliar primeiro.'

  const tableRows = useMemo<HotelPointsTableRow[]>(
    () =>
      currentRules.map((rule) => ({
        ...rule,
        scopeLabel: scopeLabels[rule.scope],
      })),
    [currentRules]
  )

  const resetModalState = () => {
    setEditingId(null)
    if (activeTab !== 'ranking') {
      setForm(defaultForm(activeTab))
    }
    setSelectedTargets([])
    setSearchQuery('')
    setSearchResults([])
    setErrors({})
    setShowSelected(true)
  }

  const loadRules = async () => {
    try {
      setTableLoading(true)
      const res = await GET(`/hotel-points-management?typePoints=${activeTab}`)
      setSpecificRules(extractList(res).map(normalizeRule))
    } catch (error) {
      console.error('Erro ao carregar regras de Pontos Hotel:', error)
    } finally {
      setTableLoading(false)
    }
  }

  const loadRanking = async () => {
    try {
      setRankingLoading(true)
      const res = await GET('/hotel-points-priority-ranking')
      const data = extractList(res)
      if (data.length > 0 && Array.isArray(data[0].rankingOrder)) {
        setRankingOrder(data[0].rankingOrder)
      } else {
        setRankingOrder(DEFAULT_RANKING)
      }
    } catch (error) {
      console.error('Erro ao carregar o ranking de prioridade:', error)
    } finally {
      setRankingLoading(false)
    }
  }

  const saveRanking = async () => {
    try {
      setRankingLoading(true)
      await POST('/hotel-points-priority-ranking', {
        rankingOrder,
      })
      Swal.fire({
        icon: 'success',
        title: 'Ranking salvo',
        text: 'A ordem de prioridade foi atualizada com sucesso.',
      })
    } catch (error) {
      console.error('Erro ao salvar ranking:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Não foi possível salvar o ranking. Tente novamente.',
      })
    } finally {
      setRankingLoading(false)
    }
  }

  const moveRankingItem = (index: number, direction: 'up' | 'down') => {
    const newRanking = [...rankingOrder]
    if (direction === 'up' && index > 0) {
      ;[newRanking[index - 1], newRanking[index]] = [newRanking[index], newRanking[index - 1]]
    } else if (direction === 'down' && index < newRanking.length - 1) {
      ;[newRanking[index + 1], newRanking[index]] = [newRanking[index], newRanking[index + 1]]
    }
    setRankingOrder(newRanking)
  }

  const init = async () => {
    try {
      if (activeTab === 'ranking') {
        await loadRanking()
      } else {
        await loadRules()
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!loading) {
      if (activeTab === 'ranking') {
        void loadRanking()
      } else {
        void loadRules()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  const searchTargets = async (scope: RuleScope, query: string): Promise<TargetItem[]> => {
    let items: any[] = []

    switch (scope) {
      case 'service': {
        const response = await POST('/hotel-service/filter', {})
        items = extractList(response).filter((item) => matchesQuery(item, query))
        break
      }
      case 'service_classification': {
        let response
        try {
          response = await GET('/service-classification')
        } catch {
          response = await POST('/service-classification/filter', {})
        }
        items = extractList(response).filter((item) => matchesQuery(item, query))
        break
      }
      case 'room_service': {
        const response = await GET('/room-service')
        items = extractList(response).filter((item) => matchesQuery(item, query))
        break
      }
      case 'experience': {
        const response = await GET('/hotel-experiences')
        items = extractList(response).filter((item) => matchesQuery(item, query))
        break
      }
      case 'activity': {
        const response = await GET('/hotel-activities')
        items = extractList(response).filter((item) => matchesQuery(item, query))
        break
      }
      case 'tour': {
        const response = await GET('/hotel-tours')
        items = extractList(response).filter((item) => matchesQuery(item, query))
        break
      }
      case 'survey': {
        try {
          const response = await GET('/survey')
          items = extractList(response)
        } catch {
          items = []
        }
        if (items.length === 0) {
          try {
            const response = await POST('/survey/filter', {})
            items = extractList(response)
          } catch {
            items = []
          }
        }
        items = items.filter((item) => matchesQuery(item, query))
        break
      }
      case 'feedback_form': {
        const response = await POST('/feedbackform/filter', {})
        items = extractList(response).filter((item) => matchesQuery(item, query))
        break
      }
      case 'review_form': {
        const response = await POST('/review-hotel-form/filter', {})
        items = extractList(response).filter((item) => matchesQuery(item, query))
        break
      }
      default:
        break
    }

    return items.map(normalizeTarget)
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      const query = searchQuery.trim()

      if (!wrapperModal.display || query.length < 2) {
        setSearchResults([])
        setSearchLoading(false)
        return
      }

      const runSearch = async () => {
        try {
          setSearchLoading(true)
          const results = await searchTargets(form.scope, query)
          setSearchResults(
            results.filter((r) => !selectedTargets.some((t) => t.id === r.id)).slice(0, 20)
          )
        } catch (error) {
          console.error('Erro ao buscar itens de Pontos Hotel:', error)
          setSearchResults([])
        } finally {
          setSearchLoading(false)
        }
      }

      void runSearch()
    }, 300)

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.scope, searchQuery, wrapperModal.display])

  const openCreateModal = (scope: RuleScope) => {
    setEditingId(null)
    setForm(defaultForm(scope))
    setSelectedTargets([])
    setSearchQuery('')
    setSearchResults([])
    setErrors({})
    showWrapperModal(`Nova regra - ${scopePageMeta[scope].title}`)
  }

  const openEditModal = (rule: HotelPointsRuleRecord) => {
    setEditingId(rule.id)
    setForm({
      scope: rule.scope,
      points: rule.points ?? 0,
      validFrom: toDateOnly(rule.validFrom),
      validTo: toDateOnly(rule.validTo),
      status: Boolean(rule.status),
    })
    setSelectedTargets([
      {
        id: rule.targetId,
        name: rule.targetName,
      },
    ])
    setSearchQuery('')
    setSearchResults([])
    setErrors({})
    showWrapperModal(`Editar regra - ${scopePageMeta[rule.scope].title}`)
  }

  const setField = <K extends keyof HotelPointsRuleForm>(
    field: K,
    value: HotelPointsRuleForm[K]
  ) => {
    setForm((prev) => ({...prev, [field]: value}))
    if (errors[field as string]) {
      const next = {...errors}
      delete next[field as string]
      setErrors(next)
    }
  }

  const validateForm = () => {
    const nextErrors: Record<string, string> = {}

    if (!form.scope) nextErrors.scope = 'Selecione o tipo do item.'
    if (selectedTargets.length === 0) nextErrors.target = 'Escolha ao menos um item alvo da regra.'

    const start = form.validFrom ? moment(form.validFrom) : null
    const end = form.validTo ? moment(form.validTo) : null

    if (start && !start.isValid()) nextErrors.validFrom = 'Data inicial inválida.'
    if (end && !end.isValid()) nextErrors.validTo = 'Data final inválida.'
    if (start && end && start.isValid() && end.isValid() && end.isBefore(start, 'day')) {
      nextErrors.validTo = 'A data final deve ser igual ou posterior à data inicial.'
    }

    const numericValue = Number(form.points)
    if (!Number.isInteger(numericValue) || numericValue < 0) {
      nextErrors.points = 'Informe um valor inteiro igual ou maior que zero.'
    }

    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const saveRule = async () => {
    if (!validateForm()) {
      Swal.fire({
        icon: 'error',
        title: 'Campos obrigatórios',
        text: 'Verifique os campos destacados antes de salvar.',
      })
      return
    }

    try {
      setSavingRule(true)

      const getPayloads = () =>
        selectedTargets.map((target) => {
          let finalValidFrom = form.validFrom
            ? moment.utc(form.validFrom).startOf('day').toISOString()
            : null
          let finalValidTo = form.validTo
            ? moment.utc(form.validTo).endOf('day').toISOString()
            : null

          if (form.validTo && !form.validFrom) {
            finalValidFrom = moment.utc().startOf('day').toISOString()
          }

          return {
            typePoints: editingId ? form.scope : (activeTab as RuleScope),
            targetId: target.id,
            points: Number(form.points),
            dateInit: finalValidFrom,
            dateEnd: finalValidTo,
            status: Boolean(form.status),
          }
        })

      if (editingId) {
        const payloads = getPayloads()
        await Promise.all(
          payloads.map((payload) => PUT(`/hotel-points-management/${editingId}`, payload))
        )
      } else {
        await POST('/hotel-points-management/multiple', getPayloads())
      }

      closeWrapperModal()
      resetModalState()
      Swal.fire({
        icon: 'success',
        title: 'Registro salvo',
        text: 'A regra foi salva com sucesso.',
      })
    } catch (error) {
      console.error('Erro ao salvar regra de Pontos Hotel:', error)
      Swal.fire({
        icon: 'warning',
        title: 'Atenção',
        text: String(error) || 'Não foi possível salvar a regra. Tente novamente.',
      })
    } finally {
      await loadRules()
      setSavingRule(false)
    }
  }

  const deleteRule = async (rule: HotelPointsRuleRecord) => {
    const confirmation = await Swal.fire({
      icon: 'warning',
      title: 'Excluir registro?',
      text: 'Essa ação não pode ser desfeita.',
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
    })

    if (!confirmation.isConfirmed) return

    try {
      setLoading(true)
      await DELETE(`/hotel-points-management/${rule.id}`)
      await loadRules()
      Swal.fire({
        icon: 'success',
        title: 'Registro removido',
      })
    } catch (error) {
      console.error('Erro ao excluir regra de Pontos Hotel:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Não foi possível excluir o registro.',
      })
    } finally {
      setLoading(false)
    }
  }

  const switchStatus = async (Id: number, status: boolean) => {
    try {
      const switchResult = await PATCH(`/hotel-points-management/management/${Id}`, {
        status: !status,
      })

      if (switchResult.success) {
        setSpecificRules((prev) =>
          prev.map((management) =>
            management.id === Id ? {...management, status: !status} : management
          )
        )
      }
    } catch (e) {
      console.log(e)
    }
  }

  const renderSearchResultCard = (item: TargetItem, onClick: () => void) => {
    return (
      <div
        key={item.id}
        className={`d-flex align-items-center gap-3 p-3 rounded-2 border border-secondary border-opacity-25 bg-white ${styles.resultItem} cursor-pointer mb-2`}
        onClick={onClick}
      >
        <div className='symbol symbol-45px symbol-fixed flex-shrink-0'>
          <div className='symbol-label bg-light-primary'>
            <i className={`bi ${scopeIcons[form.scope]} text-primary fs-4`}></i>
          </div>
        </div>
        <div className='flex-grow-1' style={{minWidth: 0}}>
          <div className='d-flex align-items-center gap-2'>
            <span className='fw-bold text-gray-800 text-truncate'>{item.name}</span>
            {item.status !== undefined && (
              <span
                className={`badge ${item.status ? 'badge-light-success' : 'badge-light-danger'}`}
              >
                {item.status ? 'Ativo' : 'Inativo'}
              </span>
            )}
          </div>
        </div>
        <span className='badge badge-light fw-normal flex-shrink-0'>ID {item.id}</span>
      </div>
    )
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>Gestão e Regras</span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  Regras específicas de pontos por item do hotel.
                </span>
              </h3>
              <div className='card-toolbar'>
                {activeTab !== 'ranking' && (
                  <Button
                    text='Nova Regra'
                    color='primary'
                    size='small'
                    icon='fas fa-plus'
                    horizontalPadding={6}
                    btnAttr={{type: 'button'}}
                    click={() => openCreateModal(activeTab as RuleScope)}
                    btnClass='fw-bolder'
                  />
                )}
              </div>
            </div>

            <div className='card-body'>
              <div className={`d-flex ${styles.tabMenu} flex-wrap`}>
                {RULE_SCOPES.map((scope) => (
                  <div
                    key={scope}
                    className={`${styles.tabItem} ${
                      activeTab === scope ? styles.tabActive : styles.tabInactive
                    }`}
                    onClick={() => setActiveTab(scope)}
                  >
                    {scopeLabels[scope]}
                  </div>
                ))}
                {/* Prioridade: ranking de regras. Desativado no hotel por agora.
                {ENABLE_PRIORITY_RANKING_TAB && (
                  <div
                    className={`${styles.tabItem} ${
                      activeTab === 'ranking' ? styles.tabActive : styles.tabInactivePriority
                    }`}
                    onClick={() => setActiveTab('ranking')}
                  >
                    Prioridade
                  </div>
                )}
                */}
              </div>

              {/* Ranking de prioridade — reativar junto com ENABLE_PRIORITY_RANKING_TAB
              {activeTab === 'ranking' ? (
                ... painel Ordem de Prioridade ...
              ) : (
              */}
              {ENABLE_PRIORITY_RANKING_TAB && activeTab === 'ranking' ? (
                <div className='d-flex flex-column gap-4'>
                  <div>
                    <h4 className='fw-bolder mb-1'>{currentTitle}</h4>
                    <div className='text-muted'>
                      Defina qual regra o sistema deve avaliar primeiro. O item no topo tem
                      prioridade máxima.
                    </div>
                  </div>

                  {rankingLoading ? (
                    <Loading />
                  ) : (
                    <div className='row'>
                      <div className='col-md-12'>
                        <div className='d-flex flex-column gap-3'>
                          {rankingOrder.map((item, index) => (
                            <div
                              key={item}
                              className='d-flex align-items-center justify-content-between p-4 border rounded bg-white shadow-sm'
                            >
                              <div className='d-flex align-items-center gap-3'>
                                <div className='badge badge-light-primary fs-5'>{index + 1}º</div>
                                <span className='fw-bolder fs-5 text-gray-800'>
                                  {rankingLabels[item] || item}
                                </span>
                              </div>
                              <div className='d-flex gap-2'>
                                <button
                                  className='btn btn-icon btn-light btn-sm'
                                  onClick={() => moveRankingItem(index, 'up')}
                                  disabled={index === 0}
                                >
                                  <i className='fas fa-chevron-up'></i>
                                </button>
                                <button
                                  className='btn btn-icon btn-light btn-sm'
                                  onClick={() => moveRankingItem(index, 'down')}
                                  disabled={index === rankingOrder.length - 1}
                                >
                                  <i className='fas fa-chevron-down'></i>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className='d-flex justify-content-end mt-10'>
                          <Button
                            text='Salvar'
                            color='primary'
                            size='small'
                            click={() => void saveRanking()}
                            disabled={rankingLoading}
                            btnClass='fw-bolder'
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className='d-flex flex-column gap-4'>
                  <div>
                    <h4 className='fw-bolder mb-1'>{currentTitle}</h4>
                    <div className='text-muted'>{currentDescription}</div>
                  </div>

                  {tableLoading ? (
                    <Loading />
                  ) : (
                    <Datatable
                      data={tableRows}
                      headers={[
                        {key: 'Item', value: 'targetName', type: 'default'},
                        {
                          key: 'Status',
                          value: 'status',
                          type: 'check',
                          action: (item: any) => switchStatus(item.id, item.status),
                        },
                        {key: 'Pontos', value: 'points', type: 'default'},
                        {key: 'Data inicial', value: 'validFrom', type: 'date'},
                        {key: 'Data final', value: 'validTo', type: 'date'},
                        {key: 'Ações', value: 'acoes', type: '', noOrder: true},
                      ]}
                      options={[
                        {
                          icon: 'fas fa-pen',
                          action: (item) => openEditModal(item),
                          title: 'Editar regra',
                        },
                        {
                          icon: 'fas fa-trash',
                          action: (item) => void deleteRule(item),
                          title: 'Excluir regra',
                        },
                      ]}
                      color='primary'
                      showControls={true}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ModalWrapper
        confirmAction={() => void saveRule()}
        shouldCloseModal={false}
        disableMainButton={savingRule}
        onCloseAction={resetModalState}
        textAction={savingRule ? 'Salvando...' : editingId ? 'Atualizar' : 'Salvar'}
        zIndex={1050}
        width={120}
      >
        <div className='row g-4 mt-3'>
          {!editingId && (
            <div>
              <div className='col-12'>
                <h6 className='fw-bold text-gray-800 mb-3'>
                  <i className={`bi ${scopeIcons[form.scope]} text-primary me-2`}></i>
                  Seleção de itens para a regra
                </h6>
              </div>
              <div className='col-md-12'>
                <Input
                  inputAttr={{type: 'text'}}
                  label='Buscar item'
                  placeholder='Digite ao menos 2 caracteres para buscar...'
                  value={searchQuery}
                  change={(value: string) => setSearchQuery(value)}
                  disabled={!!editingId}
                  marginTop='0'
                />
                <div className='text-muted small mt-1'>
                  A busca é carregada de forma assíncrona conforme a aba selecionada.
                </div>
              </div>
              {selectedTargets.length > 0 && (
                <div className='col-12'>
                  <div
                    className='d-flex justify-content-between align-items-center mb-3 cursor-pointer'
                    onClick={() => setShowSelected(!showSelected)}
                  >
                    <h6 className='fw-bold text-gray-800 mb-0'>
                      Itens Selecionados ({selectedTargets.length})
                    </h6>
                    <button type='button' className='btn btn-sm btn-icon btn-light'>
                      <i className={`bi bi-chevron-${showSelected ? 'up' : 'down'}`}></i>
                    </button>
                  </div>
                  <div
                    style={{
                      maxHeight: showSelected ? '300px' : '0px',
                      opacity: showSelected ? 1 : 0,
                      overflowY: showSelected ? 'auto' : 'hidden',
                      transition: 'all 0.3s ease-in-out',
                    }}
                  >
                    <div className='d-flex flex-wrap gap-2 pb-2'>
                      {selectedTargets.map((target) => (
                        <span
                          key={target.id}
                          className='badge badge-light-primary d-flex align-items-center py-2 px-3 fs-6'
                        >
                          <span className='me-2 text-truncate' style={{maxWidth: '250px'}}>
                            {target.name}
                          </span>
                          <span className='text-muted fw-normal fs-8 me-3'>ID: {target.id}</span>
                          {!editingId && (
                            <i
                              className='bi bi-x fs-3 cursor-pointer text-hover-danger ms-1'
                              onClick={(e) => {
                                e.stopPropagation()
                                setSelectedTargets((prev) => prev.filter((t) => t.id !== target.id))
                              }}
                            ></i>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div className='col-12'>
                <div className='d-flex justify-content-between align-items-center mb-3 mt-2'>
                  <h6 className='fw-bold text-gray-800 mb-0'>Resultados da pesquisa</h6>
                  <div className='d-flex align-items-center gap-3'>
                    {searchLoading && (
                      <span className='text-muted small d-flex align-items-center gap-2'>
                        <span
                          className='spinner-border spinner-border-sm'
                          role='status'
                          aria-hidden='true'
                        ></span>
                        Pesquisando...
                      </span>
                    )}
                    {!searchLoading && searchResults.length > 1 && (
                      <button
                        type='button'
                        className='btn btn-sm btn-light-primary py-1 px-3'
                        onClick={() => {
                          setSelectedTargets((prev) => [...prev, ...searchResults])
                          setSearchResults([])
                        }}
                      >
                        Selecionar todos
                      </button>
                    )}
                  </div>
                </div>
                <div className={`border rounded-3 p-2 bg-light ${styles.resultsScroll}`}>
                  {searchQuery.trim().length >= 2 ? (
                    searchResults.length > 0 ? (
                      <div className='d-flex flex-column gap-2'>
                        {searchResults.map((item) =>
                          renderSearchResultCard(item, () => {
                            setSelectedTargets((prev) => [...prev, item])
                            setSearchResults((prev) => prev.filter((r) => r.id !== item.id))
                          })
                        )}
                      </div>
                    ) : (
                      <div className='text-center text-muted p-4'>
                        <i className='bi bi-search fs-2 d-block mb-2'></i>
                        Nenhum item encontrado para essa pesquisa.
                      </div>
                    )
                  ) : (
                    <div className='text-center text-muted p-4'>
                      <i className='bi bi-search fs-2 d-block mb-2'></i>
                      Digite ao menos dois caracteres para localizar o item.
                    </div>
                  )}
                </div>
                {errors.target && <div className='text-danger small mt-1'>{errors.target}</div>}
              </div>
              <div className='col-12'>
                <div className='separator separator-dashed'></div>
              </div>
            </div>
          )}

          <div className='col-12 mt-5'>
            <h6 className='fw-bold text-gray-800 mb-3'>
              <i className='bi bi-star text-primary me-2'></i>
              Pontos
            </h6>
          </div>
          <div className='col-md-12'>
            <Input
              inputAttr={{type: 'number'}}
              label='Pontos'
              value={String(form.points)}
              change={(value: string) => setField('points', parseInt(value, 10) || 0)}
              minNumber={0}
              allowFloat={false}
              marginTop='0'
            />
            {errors.points && <div className='text-danger small mt-1'>{errors.points}</div>}
          </div>
          <div className='col-md-12'>
            <Input
              inputAttr={{type: 'select'}}
              label='Status'
              value={String(form.status)}
              change={(value: string) => setField('status', value === 'true')}
              options={[
                {select: 'Ativo', value: 'true'},
                {select: 'Inativo', value: 'false'},
              ]}
              marginTop='0'
            />
          </div>
          <div className='col-12'>
            <div className='separator separator-dashed'></div>
          </div>
          <div className='col-12 mt-5'>
            <h6 className='fw-bold text-gray-800 mb-3'>
              <i className='bi bi-calendar-range text-primary me-2'></i>
              Vigência (Opcional)
            </h6>
          </div>
          <div className='col-md-6'>
            <Input
              inputAttr={{type: 'date'}}
              label='Data inicial'
              value={form.validFrom}
              change={(value: string) => setField('validFrom', value)}
              marginTop='0'
            />
            {errors.validFrom && <div className='text-danger small mt-1'>{errors.validFrom}</div>}
          </div>
          <div className='col-md-6'>
            <Input
              inputAttr={{type: 'date'}}
              label='Data final'
              value={form.validTo}
              change={(value: string) => setField('validTo', value)}
              marginTop='0'
            />
            {errors.validTo && <div className='text-danger small mt-1'>{errors.validTo}</div>}
          </div>
          <div className='col-12'>
            <div className='alert alert-info d-flex align-items-start mb-0 mt-2'>
              <i className='bi bi-info-circle-fill fs-4 me-3 mt-1'></i>
              <div>
                Deixe a vigência em branco para a regra valer por tempo indeterminado.
              </div>
            </div>
          </div>
        </div>
      </ModalWrapper>
    </>
  )
}

const HotelPointsManagement: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: false},
          {title: 'Pontos Hotel', path: 'hotel-points', isActive: false},
          {title: 'Gestão e Regras', path: 'hotel-points/management', isActive: true},
        ]}
      >
        Gestão e Regras
      </PageTitle>
      <ManagementContent />
    </>
  )
}

export {HotelPointsManagement}
